import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
// styles
import styles from "./BrokerTable.module.scss";
// components
import Table from "../../common/Table";

// packages
import { useTranslation } from "react-i18next";

// types
import {
  ESenderRole,
  IContractDetailsResponseData,
  UserProfile,
} from "../../../setup/api/services/contract/models";
import useLocalStorage from "../../../hooks/useLocalStorage/useLocalStorage";
import { UserInfo } from "../../navbar";
import { EPARTY_STATUS } from "../../../enums";
import ContractActions from "../../../pages/contract/actions";
import { ContractContext } from "../../../providers/contract";
import { useParams } from "react-router-dom";
interface BrokerTableProps {
  canMakeContractActions: (userId) => boolean | undefined;
  partiesObj: Partial<UserProfile>;
  milestone: IContractDetailsResponseData;
}

export type RowData = {
  id: string;
  name: string;
  number: string;
  amount: string;
  receive: string;
  actions: () => any;
};

const BrokerTable: React.FC<BrokerTableProps> = ({
  partiesObj,
  canMakeContractActions,
  milestone,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { actions } = useContext(ContractContext);
  const [rows, setRows] = useState<RowData[]>([]);
  const [userInfo] = useLocalStorage<UserInfo | null>("userInfo", null);

  const parties = useMemo(
    () => milestone?.partiesProfiles?.BROKER,
    [milestone]
  );

  const refreshContractDetails = useCallback(() => {
    if (id) actions?.getContract();
  }, [id]);

  const columns = [
    { type: "id", value: "" },
    { type: "name", value: `${t("lbl_party")}` },
    { type: "number", value: `${t("lbl_edit_profile_phone")}` },
    { type: "amount", value: `${t("lbl_value_sar")}` },
    { type: "receive", value: `${t("lbl_loyal_receive")}` },
    { type: "action", value: "" },
  ];

  const transformPayToRows = useCallback(
    (parties): RowData[] => {
      parties?.forEach(function (party, index) {
        if (party?.phoneNumber === userInfo?.phoneNumber) {
          parties?.splice(index, 1);
          parties?.unshift(party);
        }
      });

      return parties
        ?.filter((party) => !partiesObj[`${party?.phoneNumber}`]?.arbitrator)
        ?.map((item, index) => ({
          id: `${index + 1}.`,
          name: item?.profile?.fullName || t("lbl_not_registered_yet"),
          number: item?.phoneNumber,
          amount: partiesObj[`${item?.phoneNumber}`]?.amount,
          receive: partiesObj[`${item?.phoneNumber}`]?.amount,
          action: "",
        }));
    },
    [partiesObj, t, userInfo?.phoneNumber]
  );

  useEffect(() => {
    if (parties?.length) {
      setRows(transformPayToRows(parties));
    }
  }, [parties, parties?.length, transformPayToRows]);

  const renderCell = useCallback(
    (column: string, row: unknown) => {
      const rowData = row as RowData;
      switch (column) {
        case "id":
          return (
            <p className={`${styles["p3"]} ${styles["g600"]}`}>{rowData?.id}</p>
          );
        case "name":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.name}
            </p>
          );
        case "number":
          return (
            <p
              style={{ direction: "ltr" }}
              className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.number}
            </p>
          );
        case "amount":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.amount}
            </p>
          );
        case "receive":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.receive}
            </p>
          );

        case "action":
          if (
            partiesObj[`${rowData?.number}`]?.status ===
              EPARTY_STATUS.ARBITRATOR_ACCEPT_ITEM ||
            partiesObj[`${rowData?.number}`]?.status ===
              EPARTY_STATUS.ARBITRATOR_REFUNDED_CUSTOMER
          )
            return <></>;
          return (
            <div
              className={styles["action_container"]}
              style={{ padding: "8px" }}>
              {canMakeContractActions(
                parties?.find((party) => party?.phoneNumber === rowData?.number)
              ) && (
                <ContractActions
                  receiverType={ESenderRole.BROKER}
                  handleMenuClose={refreshContractDetails}
                  contract={{
                    ...milestone,
                    receiverId: milestone?.receiverId || "-1",
                    senderId: `${
                      parties?.find(
                        (party) => party?.phoneNumber === rowData?.number
                      )?.id
                    }`,
                  }}
                  id={"broker-table"}
                />
              )}
            </div>
          );

        default:
          return <p>{rowData[column]}</p>;
      }
    },
    [
      canMakeContractActions,
      milestone,
      parties,
      partiesObj,
      refreshContractDetails,
    ]
  );

  return <Table columns={columns} rows={rows} renderCell={renderCell} />;
};

export default React.memo(BrokerTable);
